import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Library, Users, Headphones, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { getDashboardData } from "@/services/contentService";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-data"],
    queryFn: getDashboardData,
  });

  const stats = useMemo(() => {
    const contentItems = data?.contentItems || [];
    const users = data?.users || [];
    return [
      { label: "Total contenido", value: contentItems.length, icon: Library, color: "text-primary" },
      { label: "Publicados", value: contentItems.filter(c => c.status === "published").length, icon: TrendingUp, color: "text-primary" },
      { label: "Audiolibros", value: contentItems.filter(c => c.content_type === "audio" || c.content_type === "mixed").length, icon: Headphones, color: "text-accent-gold" },
      { label: "Usuarios", value: users.length, icon: Users, color: "text-primary" },
    ];
  }, [data]);

  const chartData = useMemo(() => {
    const contentItems = data?.contentItems || [];
    return [
      { name: "Audio", count: contentItems.filter(c => c.content_type === "audio").length },
      { name: "PDF", count: contentItems.filter(c => c.content_type === "pdf").length },
      { name: "Mixto", count: contentItems.filter(c => c.content_type === "mixed").length },
    ];
  }, [data]);

  const statusData = useMemo(() => {
    const contentItems = data?.contentItems || [];
    return [
      { name: "Publicado", count: contentItems.filter(c => c.status === "published").length },
      { name: "Borrador", count: contentItems.filter(c => c.status === "draft").length },
      { name: "Archivado", count: contentItems.filter(c => c.status === "archived").length },
    ];
  }, [data]);

  const activityEvents = data?.activityEvents || [];

  // QA: content categories from bridge table
  const { data: categoryMap = {} } = useQuery({
    queryKey: ["content-categories-map"],
    queryFn: async (): Promise<Record<string, string[]>> => {
      if (!isSupabaseConfigured || !supabase) return {};
      const [ccRes, catsRes] = await Promise.all([
        supabase.from("content_categories").select("content_item_id,category_id"),
        supabase.from("categories").select("id,name"),
      ]);
      if (!ccRes.data || !catsRes.data) return {};
      const nameById = new Map<string, string>();
      for (const cat of catsRes.data) nameById.set(cat.id, cat.name);
      const map: Record<string, string[]> = {};
      for (const row of ccRes.data) {
        const name = nameById.get(row.category_id);
        if (!name) continue;
        if (!map[row.content_item_id]) map[row.content_item_id] = [];
        map[row.content_item_id].push(name);
      }
      return map;
    },
  });

  // QA: RAG settings + processing status (only surface if IA is enabled)
  const { data: ragEnabled = false } = useQuery({
    queryKey: ["rag-enabled"],
    queryFn: async (): Promise<boolean> => {
      if (!isSupabaseConfigured || !supabase) return false;
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "rag_enabled")
        .maybeSingle();
      return data?.value === "true";
    },
  });

  const { data: processingMap = {} } = useQuery({
    queryKey: ["content-processing-map"],
    enabled: ragEnabled,
    queryFn: async (): Promise<Record<string, { status: string | null; chunks_count: number | null }>> => {
      if (!isSupabaseConfigured || !supabase) return {};
      const { data } = await supabase
        .from("content_processing")
        .select("content_item_id, status, chunks_count");
      const map: Record<string, { status: string | null; chunks_count: number | null }> = {};
      for (const row of (data || []) as { content_item_id: string; status: string | null; chunks_count: number | null }[]) {
        map[row.content_item_id] = { status: row.status, chunks_count: row.chunks_count };
      }
      return map;
    },
  });

  type QaIssue = { label: string; variant: "warning" | "info" };

  const qaItems = useMemo(() => {
    const contentItems = data?.contentItems || [];
    const audioAssets = data?.audioAssets || [];
    const pdfAssets = data?.pdfAssets || [];
    const audioMap = new Map(audioAssets.map((a) => [a.content_item_id, a]));
    const pdfMap = new Map(pdfAssets.map((p) => [p.content_item_id, p]));

    return contentItems.map((item) => {
      const issues: QaIssue[] = [];
      if (!item.cover_image_url) issues.push({ label: "Sin portada", variant: "warning" });
      if (!item.description_short && !item.description_long) issues.push({ label: "Sin descripción", variant: "warning" });
      if (!(categoryMap[item.id]?.length > 0) && !item.category) issues.push({ label: "Sin categoría", variant: "warning" });
      const hasAudio = (item.content_type === "audio" || item.content_type === "mixed");
      const hasPdf = (item.content_type === "pdf" || item.content_type === "mixed");
      if (hasAudio && !audioMap.get(item.id)?.audio_url) issues.push({ label: "Sin audio", variant: "warning" });
      if (hasPdf && !pdfMap.get(item.id)?.pdf_url) issues.push({ label: "Sin PDF", variant: "warning" });
      if (item.status === "draft") issues.push({ label: "Borrador", variant: "info" });

      // IA / RAG: warn only if rag_enabled and content is published
      if (ragEnabled && item.status === "published") {
        const proc = processingMap[item.id];
        if (!proc || proc.status == null) {
          issues.push({ label: "Sin procesar IA", variant: "warning" });
        } else if (proc.status === "failed") {
          issues.push({ label: "IA: falló procesamiento", variant: "warning" });
        } else if (proc.status === "processing") {
          issues.push({ label: "IA: procesando…", variant: "info" });
        }
        // status === "completed" → no issue
      }

      return { id: item.id, title: item.title, issues };
    }).filter((item) => item.issues.length > 0);
  }, [data, categoryMap, ragEnabled, processingMap]);

  // Global RAG warning: published content with rag enabled but no processed items
  const publishedUnprocessedCount = useMemo(() => {
    if (!ragEnabled) return 0;
    const contentItems = data?.contentItems || [];
    return contentItems.filter(
      (item) =>
        item.status === "published" &&
        (!processingMap[item.id] || processingMap[item.id]?.status !== "completed"),
    ).length;
  }, [data, ragEnabled, processingMap]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-foreground">Dashboard</h1>
      </div>

      {ragEnabled && publishedUnprocessedCount > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-semibold text-amber-900">
              Asistente IA activado, pero hay {publishedUnprocessedCount} {publishedUnprocessedCount === 1 ? "contenido publicado sin procesar" : "contenidos publicados sin procesar"}
            </p>
            <p className="text-sm text-amber-800">
              El chat no podrá citar ni buscar dentro de estos contenidos hasta que los proceses manualmente.
              Abre cada contenido y haz click en <strong>"Procesar para IA"</strong> desde la card "Asistente IA".
            </p>
            <Link to="/docs?doc=guide-admin-rag" className="inline-block text-sm text-amber-900 underline hover:text-amber-700 font-medium">
              Ver guía paso a paso →
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{isLoading ? "-" : s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-sm font-heading">Contenido por tipo</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(160, 45%, 22%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm font-heading">Por estado</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusData}>
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(38, 85%, 52%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            {qaItems.length > 0 ? (
              <><AlertTriangle className="h-4 w-4 text-amber-500" />QA de contenido — {qaItems.length} con pendientes</>
            ) : (
              <><CheckCircle className="h-4 w-4 text-primary" />QA de contenido — Todo completo</>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {qaItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todos los contenidos tienen metadatos completos.</p>
          ) : (
            <div className="space-y-3">
              {qaItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <Link to={`/admin/content/${item.id}`} className="text-sm font-medium text-foreground hover:text-primary">
                    {item.title}
                  </Link>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    {item.issues.map((issue) => (
                      <span
                        key={issue.label}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          issue.variant === "warning"
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-blue-50 text-blue-600 border border-blue-200"
                        }`}
                      >
                        {issue.label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm font-heading">Actividad reciente</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activityEvents.slice(0, 8).map(ev => (
              <div key={ev.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <p className="text-sm text-foreground">{ev.description}</p>
                  <p className="text-xs text-muted-foreground">{new Date(ev.timestamp).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{ev.type.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
